import { ApiClient } from '../api-client';
import {
  googleMapsPlaceDetailsUrl,
  googleMapsPlacesUrl,
  googleMapsReverseGeocodeUrl,
} from '../urls';

import {
  GooglePlaceData,
  GooglePlaceDetail,
  GoogleReverseGeocodeResponse,
} from './types';

/**
 * This api client is used to fetch data from the Google Maps API.
 * It is a singleton class that uses the Axios instance to make requests.
 * The class is initialized with the base URL of the Google Maps API.
 */
export class GoogleMapsAPIClient extends ApiClient {
  private static classInstance?: GoogleMapsAPIClient;

  private constructor() {
    super({
      apiBaseURL: 'GOOGLE_MAPS_ENDPOINT',
    });
  }

  /**
   * Singleton pattern to reuse the Axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new GoogleMapsAPIClient();
    }
    return this.classInstance;
  };

  /**
   * Fetches Google Places autocomplete results.
   */
  public fetchPlaces = async (
    searchKey: string,
    language: 'en'
  ): Promise<GooglePlaceData[]> => {
    const response = await this.get<{ predictions: GooglePlaceData[] }>(
      googleMapsPlacesUrl({
        searchKey,
        language,
      }),
      {
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    return response.data.predictions;
  };

  /**
   * Fetches Google Place details by place ID.
   */
  public fetchPlaceDetails = async (
    placeId: string,
    language: 'en'
  ): Promise<GooglePlaceDetail> => {
    const response = await this.get<{ result: GooglePlaceDetail }>(
      googleMapsPlaceDetailsUrl({
        placeId,
        language,
      }),
      {
        requiresAuth: false,
      }
    );
    if (!response.success) throw response;
    return response.data.result;
  };

  /**
   * Reverse geocodes coordinates (lat/lng) to get address information.
   */
  public reverseGeocode = async (
    lat: number,
    lng: number,
    language: 'en'
  ): Promise<GoogleReverseGeocodeResponse> => {
    const response = await this.get<GoogleReverseGeocodeResponse>(
      googleMapsReverseGeocodeUrl({
        lat,
        lng,
        language,
      }),
      {
        requiresAuth: false,
      }
    );
    if (!response.success) throw response;
    return response.data;
  };
}

/**
 * Export the singleton instance of the GoogleMapsAPIClient.
 */
export const GOOGLE_MAPS_API = GoogleMapsAPIClient.getClientInstance();
